// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import styles from '@app/features/user/components/modals/tabs/privacy_safety_tab/DataDeletionTab.module.css';
import Users from '@app/features/user/state/Users';
import * as LocaleUtils from '@app/features/user/utils/LocaleUtils';
import * as FormUtils from '@app/lib/forms';
import {getFormattedDateTime} from '@app/vendor/date_utils/DateFormatting';
import {formatNumber} from '@app/vendor/number_utils/NumberFormatting';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useState} from 'react';

const DeleteAllMessagesModal = observer(() => {
	const {t, i18n} = useLingui();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const handleConfirm = async () => {
		setIsSubmitting(true);
		try {
			await UserCommands.bulkDeleteAllMessages();
			ModalCommands.pop();
		} catch (error) {
			FormUtils.pushApiErrorModal(i18n, error);
		} finally {
			setIsSubmitting(false);
		}
	};
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Delete All Messages`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<div className={styles.infoSection}>
						<Modal.Description>
							<Trans>Are you sure you want to delete all your messages? This action cannot be undone.</Trans>
						</Modal.Description>
						<div className={styles.infoBox}>
							<p className={styles.infoBoxTitle}>
								<Trans>What happens next:</Trans>
							</p>
							<ul className={styles.infoList}>
								<li>
									<Trans>Your deletion request will be queued and processed in the background</Trans>
								</li>
								<li>
									<Trans>The job starts 24 hours after you confirm and can be canceled or restarted anytime</Trans>
								</li>
								<li>
									<Trans>The time to complete depends on how many messages you have sent</Trans>
								</li>
								<li>
									<Trans>Once deleted, your messages cannot be recovered</Trans>
								</li>
							</ul>
						</div>
					</div>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={ModalCommands.pop} variant="secondary">
					<Trans>Cancel</Trans>
				</Button>
				<Button onClick={handleConfirm} submitting={isSubmitting} variant="danger-primary">
					<Trans>Delete All Messages</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
export const DataDeletionTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const [isCancelling, setIsCancelling] = useState(false);
	const handleDeleteAllMessages = () => {
		ModalCommands.push(modal(() => <DeleteAllMessagesModal />));
	};
	const user = Users.currentUser;
	const pending = user?.getPendingBulkMessageDeletion();
	const locale = LocaleUtils.getCurrentLocale();
	const formattedChannelCount = formatNumber(pending?.channelCount ?? 0, locale);
	const formattedMessageCount = formatNumber(pending?.messageCount ?? 0, locale);
	const scheduledAtLabel = pending?.scheduledAt ? getFormattedDateTime(pending.scheduledAt, locale) : '';
	const handleCancelPendingDeletion = useCallback(async () => {
		setIsCancelling(true);
		try {
			await UserCommands.cancelBulkDeleteAllMessages();
			ToastCommands.createToast({type: 'success', children: t`Pending deletion canceled`});
		} catch (error) {
			ToastCommands.error(t`Failed to cancel the deletion. Please try again.`);
			throw error;
		} finally {
			setIsCancelling(false);
		}
	}, []);
	return (
		<div className={styles.deleteSection}>
			{pending && (
				<WarningAlert
					title={<Trans>Pending Deletion</Trans>}
					actions={
						<Button variant="secondary" onClick={handleCancelPendingDeletion} disabled={isCancelling}>
							<Trans>Cancel pending deletion</Trans>
						</Button>
					}
				>
					<Trans>
						Deletion will remove <strong>{formattedMessageCount}</strong> messages from{' '}
						<strong>{formattedChannelCount}</strong> channels. Scheduled to run on <strong>{scheduledAtLabel}</strong>.
					</Trans>
				</WarningAlert>
			)}
			<Modal.Description className={styles.warningText}>
				<Trans>
					Once deleted, your messages cannot be recovered. The deletion process runs in the background and may take some
					time depending on how many messages you have sent.
				</Trans>
			</Modal.Description>
			{!pending && (
				<Button variant="danger-primary" fitContainer={false} onClick={handleDeleteAllMessages}>
					<Trans>Delete all my messages</Trans>
				</Button>
			)}
		</div>
	);
});
